import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api.js";
import type { ActionInput } from "@mma/shared";

// Optimistic action queue: poster actions så hurtigt som muligt, men hvis
// netværket fejler beholder vi dem i en kø og prøver igen. Hver enqueue
// får et client_id så server kan dedupe ved retry.
//
// Fejl bliver synlige i UI'et via `lastError` så spotteren kan se hvis
// noget er galt (auth, netværk, etc.).

interface Pending {
  clientId: string;
  fightId: string;
  payload: ActionInput;
  attempts: number;
}

export function useActionQueue() {
  const queueRef = useRef<Pending[]>([]);
  const sendingRef = useRef(false);
  const [pending, setPending] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [authFailed, setAuthFailed] = useState(false);

  const refreshPending = useCallback(() => {
    setPending(queueRef.current.length);
  }, []);

  const flush = useCallback(async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const item = queueRef.current[0]!;
        try {
          await api.post(`/api/actions?fightId=${item.fightId}`, {
            ...item.payload,
            client_id: item.clientId,
          });
          queueRef.current.shift();
          refreshPending();
          setLastError(null);
          setAuthFailed(false);
        } catch (err) {
          item.attempts++;
          if (err instanceof ApiError) {
            const body =
              typeof err.body === "object" && err.body !== null
                ? JSON.stringify(err.body)
                : String(err.body);
            setLastError(`HTTP ${err.status}: ${body}`);
            console.error("[queue] POST failed", err.status, err.body);
            // 401/403 → spotter skal logge ind igen. Stop med at retry
            // for ikke at hamre serveren mens vi venter på re-auth.
            if (err.status === 401 || err.status === 403) {
              setAuthFailed(true);
              break;
            }
          } else {
            const msg = err instanceof Error ? err.message : String(err);
            setLastError(`Network: ${msg}`);
            console.error("[queue] network error", err);
          }
          const wait = Math.min(5000, 250 * 2 ** Math.min(item.attempts, 5));
          await new Promise((r) => setTimeout(r, wait));
        }
      }
    } finally {
      sendingRef.current = false;
    }
  }, [refreshPending]);

  useEffect(() => {
    if (pending > 0) flush();
  }, [pending, flush]);

  // Re-flush ved fokus / online — fanger sider der har stået åbne i
  // baggrunden eller iPad efter network drop.
  useEffect(() => {
    const onFocus = () => {
      setAuthFailed(false); // antag user re-auth'd hvis de er tilbage
      flush();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onFocus);
    };
  }, [flush]);

  const enqueue = useCallback(
    (fightId: string, payload: ActionInput) => {
      const clientId =
        crypto.randomUUID?.() ??
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      queueRef.current.push({ clientId, fightId, payload, attempts: 0 });
      refreshPending();
      return clientId;
    },
    [refreshPending],
  );

  return { enqueue, pending, lastError, authFailed };
}
